import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import EntityDocuments from "@/components/EntityDocuments";
import { toast } from "sonner";
import { UserRound, Save, ArrowLeft, FileText } from "lucide-react";

const DEFAULT = {
  firstName: "", lastName: "", birthDate: "", licenseType: "PPL(A)",
  licenseNumber: "", weight: 80, email: "", phone: "", notes: "",
};

const PILOT_DOCS = [
  { key: "licence", label: "Licence (PPL/LAPL)" },
  { key: "medical", label: "Certificat médical" },
];

const Field = ({ label, unit, ...props }) => (
  <div>
    <Label className="text-[11px] uppercase tracking-wider text-slate-400">
      {label} {unit && <span className="text-slate-600">({unit})</span>}
    </Label>
    <Input {...props} className="mt-1 border-slate-700 bg-slate-900/60 text-slate-100" />
  </div>
);

export default function PilotForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(DEFAULT);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (id) api.get(`/pilots/${id}`).then((r) => setForm({ ...DEFAULT, ...r.data }));
  }, [id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.lastName && !form.firstName) return toast.error("Nom / prénom requis");
    setSaving(true);
    try {
      if (id) {
        await api.put(`/pilots/${id}`, form);
        toast.success("Pilote enregistré");
      } else {
        const { data } = await api.post("/pilots", form);
        toast.success("Pilote créé — vous pouvez joindre les documents");
        navigate(`/pilots/${data.id}`);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <button onClick={() => navigate("/pilots")} className="flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-300">
          <ArrowLeft className="h-4 w-4" /> Retour
        </button>
        <Button data-testid="save-pilot-button" onClick={save} disabled={saving}
          className="bg-cyan-500 font-semibold text-[#070C18] hover:bg-cyan-400">
          <Save className="mr-1.5 h-4 w-4" /> Enregistrer
        </Button>
      </div>

      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-cyan-500/80">
        <UserRound className="h-3.5 w-3.5" /> {id ? "Modifier le pilote" : "Nouveau pilote"}
      </div>

      <div className="glass rounded-2xl p-6">
        <h2 className="mb-4 flex items-center gap-2 font-display text-base font-semibold uppercase tracking-wide text-cyan-400">
          <UserRound className="h-4 w-4" /> Informations pilote
        </h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Prénom" value={form.firstName} onChange={(e) => set("firstName", e.target.value)} data-testid="pilot-firstname-input" />
          <Field label="Nom" value={form.lastName} onChange={(e) => set("lastName", e.target.value)} data-testid="pilot-lastname-input" />
          <Field label="Date de naissance" type="date" value={form.birthDate} onChange={(e) => set("birthDate", e.target.value)} data-testid="pilot-birth-input" />
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-400">Type de licence</Label>
            <Select value={form.licenseType} onValueChange={(v) => set("licenseType", v)}>
              <SelectTrigger data-testid="pilot-licensetype-select" className="mt-1 border-slate-700 bg-slate-900/60 text-slate-100">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-slate-700 bg-[#0D1527] text-slate-100">
                {["PPL(A)", "LAPL(A)", "CPL(A)", "ULM", "Autre"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Field label="N° de licence" value={form.licenseNumber} onChange={(e) => set("licenseNumber", e.target.value)} data-testid="pilot-licensenumber-input" />
          <Field label="Poids" unit="kg" type="number" value={form.weight} onChange={(e) => set("weight", e.target.value === "" ? "" : Number(e.target.value))} data-testid="pilot-weight-input" />
          <Field label="Email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
          <Field label="Téléphone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
        </div>
        <div className="mt-4">
          <Label className="text-[11px] uppercase tracking-wider text-slate-400">Notes</Label>
          <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2}
            className="mt-1 border-slate-700 bg-slate-900/60 text-slate-100" />
        </div>
        <p className="mt-3 text-xs text-slate-500">Le poids est utilisé pour le devis de masse et centrage dans les briefings.</p>
      </div>

      <div className="glass rounded-2xl p-6">
        <h2 className="mb-4 flex items-center gap-2 font-display text-base font-semibold uppercase tracking-wide text-cyan-400">
          <FileText className="h-4 w-4" /> Documents pilote (avec date de validité)
        </h2>
        <EntityDocuments entityType="pilots" entityId={id} docTypes={PILOT_DOCS} />
      </div>
    </div>
  );
}
